import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './Login.css';

export default function Register() {
  const [step, setStep] = useState('register'); // 'register' | 'verify'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [tenantName, setTenantName] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const { register, confirmEmail, resendVerificationCode } = useAuth();
  const navigate = useNavigate();
  const timerRef = useRef(null);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => clearTimeout(timerRef.current);
  }, []);

  // Resend cooldown countdown
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const interval = setInterval(() => {
      setResendCooldown((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [resendCooldown]);

  const validatePassword = (pwd) => {
    if (pwd.length < 8) return 'Password must be at least 8 characters';
    return null;
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    setIsLoading(true);

    try {
      await register(email, password, tenantName);
      setSuccess('Verification code sent to your email.');
      setStep('verify');
    } catch (err) {
      console.error('[Register] Error:', err);
      const apiError = err.response?.data?.error;
      if (apiError) {
        const errorMessages = {
          USER_EXISTS: 'An account with this email already exists. Please sign in.',
          INVALID_PASSWORD: 'Password does not meet requirements. Must include uppercase, lowercase, numbers, and special characters.',
          INVALID_PARAMETER: 'Invalid registration parameters. Please check your input.',
        };
        setError(errorMessages[apiError.code] || apiError.message || 'Registration failed');
      } else {
        setError(err.message || 'Registration failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await confirmEmail(email, verificationCode.trim());
      setSuccess('Email verified successfully!');
      timerRef.current = setTimeout(() => {
        navigate('/login', { state: { message: 'Email verified. Please sign in.' } });
      }, 1500);
    } catch (err) {
      console.error('[Register] Verification error:', err);
      const messages = {
        CodeMismatchException: 'Invalid verification code.',
        ExpiredCodeException: 'Code has expired. Please request a new one.',
        NotAuthorizedException: 'Verification failed. Your account may already be verified — try signing in.',
        LimitExceededException: 'Too many attempts. Please wait a few minutes.',
      };
      setError(messages[err.code] || err.message || 'Verification failed.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError('');
    setSuccess('');
    setIsLoading(true);

    try {
      await resendVerificationCode(email);
      setSuccess('New verification code sent to your email.');
      setResendCooldown(60);
    } catch (err) {
      console.error('[Register] Resend error:', err);
      const messages = {
        LimitExceededException: 'Too many attempts. Please wait a few minutes.',
        InvalidParameterException: 'This account is already verified. Please sign in.',
      };
      setError(messages[err.code] || 'Failed to resend code.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h1>{step === 'register' ? 'Create Account' : 'Verify Email'}</h1>
        <p className="auth-subtitle">
          {step === 'register'
            ? 'Start your freight quoting journey'
            : `Enter the code sent to ${email}`}
        </p>

        {error && <div className="auth-error">{error}</div>}
        {success && <div className="auth-success">{success}</div>}

        {step === 'register' ? (
          <form onSubmit={handleRegister}>
            <div className="form-group">
              <label htmlFor="tenantName">Company Name</label>
              <input
                id="tenantName"
                type="text"
                value={tenantName}
                onChange={(e) => setTenantName(e.target.value)}
                required
                disabled={isLoading}
                placeholder="Your company name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isLoading}
                autoComplete="email"
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isLoading}
                autoComplete="new-password"
                placeholder="Minimum 8 characters"
              />
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={isLoading}
                autoComplete="new-password"
              />
            </div>

            <button type="submit" className="btn-primary" disabled={isLoading}>
              {isLoading ? 'Creating account...' : 'Create Account'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerify}>
            <div className="form-group">
              <label htmlFor="verificationCode">Verification Code</label>
              <input
                id="verificationCode"
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                required
                disabled={isLoading}
                placeholder="Enter 6-digit code"
                autoComplete="one-time-code"
              />
            </div>

            <button type="submit" className="btn-primary" disabled={isLoading}>
              {isLoading ? 'Verifying...' : 'Verify Email'}
            </button>

            <div className="auth-links">
              <span>Didn't receive the code? </span>
              <button
                type="button"
                onClick={handleResendCode}
                disabled={isLoading || resendCooldown > 0}
                style={{
                  background: 'none',
                  border: 'none',
                  color: resendCooldown > 0 ? 'var(--text-muted)' : 'var(--primary-color)',
                  cursor: resendCooldown > 0 ? 'default' : 'pointer',
                  fontSize: '14px',
                  padding: 0,
                  textDecoration: resendCooldown > 0 ? 'none' : 'underline',
                }}
              >
                {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
              </button>
            </div>
          </form>
        )}

        {step === 'register' && (
          <div className="auth-links">
            <span>Already have an account? </span>
            <Link to="/login">Sign in</Link>
          </div>
        )}
      </div>
    </div>
  );
}
